import React, { useState, useRef, useEffect } from 'react';

interface Entry {
  id: number;
  date: string;
  bodyFatPercent: string;
  weight: string | null;
  height: string | null;
  photo: string | null;
  notes: string;
  source: string;
  aiConfidence: string | null;
  aiReasoning: string | null;
}

export default function App() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
  const [bodyFatPercent, setBodyFatPercent] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [notes, setNotes] = useState('');
  const [view, setView] = useState('log');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiEstimate, setAiEstimate] = useState<string | null>(null);
  const [aiConfidence, setAiConfidence] = useState<string | null>(null);
  const [aiReasoning, setAiReasoning] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem('bodyFatEntries');
    if (saved) setEntries(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem('bodyFatEntries', JSON.stringify(entries));
  }, [entries]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    // Compress the image before setting it
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 800;
      let width = img.width;
      let height = img.height;
      
      if (width > height && width > maxSize) {
        height = (height * maxSize) / width;
        width = maxSize;
      } else if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }
      
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')?.drawImage(img, 0, 0, width, height);
      setCurrentPhoto(canvas.toDataURL('image/jpeg', 0.7));
      resetAiState();
    };
    img.src = reader.result as string;
  };
  reader.readAsDataURL(file);
};

  const resetAiState = () => {
    setAiEstimate(null);
    setAiConfidence(null);
    setAiReasoning('');
    setError('');
  };

  const startCamera = async () => {
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
    console.log("✅ Got stream:", mediaStream);

    if (videoRef.current) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.muted = true;
      videoRef.current.playsInline = true;
      await videoRef.current.play().catch(err =>
        console.warn("⚠️ Autoplay blocked:", err)
      );
    }

    setStream(mediaStream);
    setView("camera");
  } catch (err) {
    console.error("❌ Camera error:", err);
    setError("Camera access failed. Please allow permissions and reload.");
  }
};


  const capturePhoto = () => {
  if (videoRef.current) {
    const canvas = document.createElement('canvas');
    const maxSize = 800;
    let width = videoRef.current.videoWidth;
    let height = videoRef.current.videoHeight;
    
    if (width > height && width > maxSize) {
      height = (height * maxSize) / width;
      width = maxSize;
    } else if (height > maxSize) {
      width = (width * maxSize) / height;
      height = maxSize;
    }
    
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0, width, height);
    setCurrentPhoto(canvas.toDataURL('image/jpeg', 0.7));
    stopCamera();
    setView('log');
    resetAiState();
  }
};

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const analyzePhoto = async () => {
    if (!currentPhoto) return;

    setIsAnalyzing(true);
    setError('');

    try {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: currentPhoto,
          height: height || null,
          weight: weight || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Analysis failed');
      }

      setAiEstimate(data.estimate?.toString() || null);
      setAiConfidence(data.confidence || 'unknown');
      setAiReasoning(data.reasoning || '');

    } catch (err: any) {
      setError(err.message || 'Failed to analyze image. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveEntry = () => {
    if (!bodyFatPercent && !aiEstimate) {
      setError('Please enter a body fat percentage or get an AI estimate');
      return;
    }

    const newEntry: Entry = {
      id: Date.now(),
      date: new Date().toISOString(),
      bodyFatPercent: bodyFatPercent || aiEstimate || '',
      weight: weight || null,
      height: height || null,
      photo: currentPhoto,
      notes,
      source: bodyFatPercent ? 'manual' : 'ai-estimate',
      aiConfidence: bodyFatPercent ? null : aiConfidence,
      aiReasoning: bodyFatPercent ? null : aiReasoning
    };

    setEntries([newEntry, ...entries]);
    setCurrentPhoto(null);
    setBodyFatPercent('');
    setWeight('');
    setNotes('');
    resetAiState();
  };

  const deleteEntry = (id: number) => {
    setEntries(entries.filter(e => e.id !== id));
  };

  const getProgressColor = (bf: string) => {
    const num = parseFloat(bf);
    if (num < 15) return '#22c55e';
    if (num < 20) return '#84cc16';
    if (num < 25) return '#eab308';
    if (num < 30) return '#f97316';
    return '#ef4444';
  };

  const getConfidenceColor = (conf: string | null) => {
    if (conf === 'high') return '#22c55e';
    if (conf === 'medium') return '#eab308';
    return '#ef4444';
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Body Fat Tracker</h1>
        <p style={styles.subtitle}>AI-powered body composition analysis</p>
      </header>

      <nav style={styles.nav}>
        <button 
          style={{...styles.navButton, ...(view === 'log' ? styles.navButtonActive : {})}}
          onClick={() => { stopCamera(); setView('log'); }}
        >
          Log Entry
        </button>
        <button 
          style={{...styles.navButton, ...(view === 'history' ? styles.navButtonActive : {})}}
          onClick={() => { stopCamera(); setView('history'); }}
        >
          History ({entries.length})
        </button>
      </nav>

      <main style={styles.main}>
        {error && (
          <div style={styles.errorBanner}>
            {error}
            <button style={styles.dismissError} onClick={() => setError('')}>✕</button>
          </div>
        )}

        {view === 'camera' && (
          <div style={styles.cameraContainer}>
            <video
  ref={videoRef}
  autoPlay
  playsInline
  muted
  style={styles.video}
/>

            <p style={styles.cameraTip}>Tip: Stand in good lighting, front-facing, with minimal clothing for best results</p>
            <div style={styles.cameraControls}>
              <button style={styles.captureButton} onClick={capturePhoto}>
                📸 Capture
              </button>
              <button style={styles.secondaryButton} onClick={() => { stopCamera(); setView('log'); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {view === 'log' && (
          <div style={styles.logForm}>
            <div style={styles.photoSection}>
              {currentPhoto ? (
                <div style={styles.photoPreview}>
                  <img src={currentPhoto} alt="Preview" style={styles.previewImage} />
                  <button 
                    style={styles.removePhoto}
                    onClick={() => { setCurrentPhoto(null); resetAiState(); }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div style={styles.photoPlaceholder}>
                  <p style={styles.placeholderText}>Add a progress photo</p>
                  <p style={styles.photoTip}>For best AI results: good lighting, front-facing, minimal clothing</p>
                  <div style={styles.photoButtons}>
                    <button style={styles.primaryButton} onClick={startCamera}>
                      📷 Take Photo
                    </button>
                    <button 
                      style={styles.secondaryButton}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      📁 Upload
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
            </div>

            <div style={styles.measurementsRow}>
              <div style={styles.inputGroupSmall}>
                <label style={styles.label}>Height (in)</label>
                <input
                  type="number"
                  placeholder="70"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroupSmall}>
                <label style={styles.label}>Weight (lbs)</label>
                <input
                  type="number"
                  placeholder="175"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            {currentPhoto && (
              <div style={styles.aiSection}>
                <button 
                  style={{...styles.analyzeButton, ...(isAnalyzing ? styles.analyzeButtonDisabled : {})}}
                  onClick={analyzePhoto}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? 'Analyzing with GPT-4 Vision...' : '🤖 Analyze with AI'}
                </button>
                
                {aiEstimate && (
                  <div style={styles.aiResult}>
                    <div style={styles.aiResultHeader}>
                      <div>
                        <span style={styles.aiLabel}>AI Estimate</span>
                        <span style={{...styles.aiValue, color: getProgressColor(aiEstimate)}}>
                          {aiEstimate}%
                        </span>
                      </div>
                      <div style={styles.confidenceBadge}>
                        <span style={{...styles.confidenceDot, backgroundColor: getConfidenceColor(aiConfidence)}}></span>
                        {aiConfidence} confidence
                      </div>
                    </div>
                    
                    {aiReasoning && (
                      <p style={styles.aiReasoning}>{aiReasoning}</p>
                    )}
                    
                    <button 
                      style={styles.useEstimate}
                      onClick={() => setBodyFatPercent(aiEstimate)}
                    >
                      Use this estimate
                    </button>
                  </div>
                )}
                
                <p style={styles.disclaimer}>
                  ⚠️ AI estimates are approximations (typically ±3-5% accuracy). For precise measurements, use DEXA scan, calipers, or hydrostatic weighing.
                </p>
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Body Fat % {aiEstimate && !bodyFatPercent && '(or use AI estimate above)'}</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 18.5"
                value={bodyFatPercent}
                onChange={(e) => setBodyFatPercent(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Notes (optional)</label>
              <textarea
                placeholder="How are you feeling? Diet/exercise changes?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={styles.textarea}
              />
            </div>

            <button style={styles.saveButton} onClick={saveEntry}>
              Save Entry
            </button>
          </div>
        )}

        {view === 'history' && (
          <div style={styles.history}>
            {entries.length === 0 ? (
              <div style={styles.emptyState}>
                <p>No entries yet. Start tracking your progress!</p>
                <button style={styles.primaryButton} onClick={() => setView('log')}>
                  Add First Entry
                </button>
              </div>
            ) : (
              <>
                <div style={styles.stats}>
                  <div style={styles.statCard}>
                    <span style={styles.statLabel}>Latest</span>
                    <span style={{...styles.statValue, color: getProgressColor(entries[0].bodyFatPercent)}}>
                      {entries[0].bodyFatPercent}%
                    </span>
                  </div>
                  {entries.length > 1 && (
                    <div style={styles.statCard}>
                      <span style={styles.statLabel}>Change</span>
                      <span style={styles.statValue}>
                        {(parseFloat(entries[0].bodyFatPercent) - parseFloat(entries[entries.length - 1].bodyFatPercent)) > 0 ? '+' : ''}
                        {(parseFloat(entries[0].bodyFatPercent) - parseFloat(entries[entries.length - 1].bodyFatPercent)).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  <div style={styles.statCard}>
                    <span style={styles.statLabel}>Entries</span>
                    <span style={styles.statValue}>{entries.length}</span>
                  </div>
                </div>

                <div style={styles.entryList}>
                  {entries.map((entry) => (
                    <div key={entry.id} style={styles.entryCard}>
                      <div style={styles.entryHeader}>
                        <span style={styles.entryDate}>
                          {new Date(entry.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <span style={{...styles.entryBf, color: getProgressColor(entry.bodyFatPercent)}}>
                          {entry.bodyFatPercent}%
                        </span>
                      </div>
                      {entry.photo && (
                        <img src={entry.photo} alt="Progress" style={styles.entryPhoto} />
                      )}
                      <div style={styles.entryDetails}>
                        {entry.weight && <span>Weight: {entry.weight} lbs</span>}
                        {entry.height && <span>Height: {entry.height} in</span>}
                        {entry.source === 'ai-estimate' && (
                          <span style={{...styles.aiTag, backgroundColor: getConfidenceColor(entry.aiConfidence)}}>
                            AI ({entry.aiConfidence})
                          </span>
                        )}
                      </div>
                      {entry.aiReasoning && (
                        <p style={styles.entryReasoning}>AI: {entry.aiReasoning}</p>
                      )}
                      {entry.notes && <p style={styles.entryNotes}>{entry.notes}</p>}
                      <button 
                        style={styles.deleteButton}
                        onClick={() => deleteEntry(entry.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    padding: '24px 20px 16px',
    textAlign: 'center',
    borderBottom: '1px solid #1e293b',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#f8fafc',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: '#94a3b8',
  },
  nav: {
    display: 'flex',
    gap: '8px',
    padding: '16px 20px',
    borderBottom: '1px solid #1e293b',
  },
  navButton: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#1e293b',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  navButtonActive: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
  },
  main: {
    padding: '20px',
    maxWidth: '500px',
    margin: '0 auto',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#dc2626',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  dismissError: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
  },
  logForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  photoSection: {
    width: '100%',
  },
  photoPlaceholder: {
    border: '2px dashed #334155',
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  placeholderText: {
    margin: '0 0 8px',
    color: '#94a3b8',
    fontSize: '16px',
  },
  photoTip: {
    margin: '0 0 16px',
    color: '#64748b',
    fontSize: '13px',
  },
  photoButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  photoPreview: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  previewImage: {
    width: '100%',
    display: 'block',
    borderRadius: '12px',
  },
  removePhoto: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
  },
  cameraContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  video: {
    width: '100%',
    borderRadius: '12px',
    backgroundColor: '#000',
  },
  cameraTip: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '13px',
    margin: 0,
  },
  cameraControls: {
    display: 'flex',
    gap: '12px',
  },
  captureButton: {
    flex: 1,
    padding: '16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  measurementsRow: {
    display: 'flex',
    gap: '12px',
  },
  inputGroupSmall: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  aiSection: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '16px',
  },
  analyzeButton: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  analyzeButtonDisabled: {
    backgroundColor: '#6b7280',
    cursor: 'not-allowed',
  },
  aiResult: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#0f172a',
    borderRadius: '8px',
  },
  aiResultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  aiLabel: {
    display: 'block',
    color: '#94a3b8',
    fontSize: '13px',
    marginBottom: '4px',
  },
  aiValue: {
    fontSize: '32px',
    fontWeight: '700',
  },
  confidenceBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#94a3b8',
    textTransform: 'capitalize',
  },
  confidenceDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  aiReasoning: {
    margin: '0 0 12px',
    fontSize: '14px',
    color: '#94a3b8',
    lineHeight: '1.5',
    fontStyle: 'italic',
  },
  useEstimate: {
    width: '100%',
    padding: '10px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  disclaimer: {
    margin: '12px 0 0',
    fontSize: '12px',
    color: '#64748b',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#94a3b8',
  },
  input: {
    padding: '14px',
    border: '1px solid #334155',
    borderRadius: '8px',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: '16px',
  },
  textarea: {
    padding: '14px',
    border: '1px solid #334155',
    borderRadius: '8px',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: '16px',
    minHeight: '80px',
    resize: 'vertical',
  },
  primaryButton: {
    padding: '12px 20px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 20px',
    border: '1px solid #334155',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  history: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b',
  },
  stats: {
    display: 'flex',
    gap: '12px',
  },
  statCard: {
    flex: 1,
    padding: '16px',
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    textAlign: 'center',
  },
  statLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '4px',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#f8fafc',
  },
  entryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  entryCard: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '16px',
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  entryDate: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  entryBf: {
    fontSize: '20px',
    fontWeight: '700',
  },
  entryPhoto: {
    width: '100%',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  entryDetails: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    fontSize: '14px',
    color: '#94a3b8',
  },
  aiTag: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#fff',
  },
  entryReasoning: {
    margin: '8px 0 0',
    fontSize: '13px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  entryNotes: {
    margin: '12px 0 0',
    fontSize: '14px',
    color: '#cbd5e1',
    lineHeight: '1.5',
  },
  deleteButton: {
    marginTop: '12px',
    padding: '8px 12px',
    border: '1px solid #dc2626',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: '#dc2626',
    fontSize: '13px',
    cursor: 'pointer',
  },
};
